import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdBusiness, MdCameraAlt, MdNotifications, MdPerson, MdVerifiedUser } from 'react-icons/md';
import { postFileUpload, putData } from '../../Webservice/Webservice';
import { BUSINESS_TYPES, FONT, GST_RX, PAN_RX, authToken, initials } from './customerShared';

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(255, 209, 2)' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(245, 216, 87, 1)' },
};

const Section = ({ title, hint, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{hint}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const emptyForm = {
  name: '', email: '', mobile: '', gender: '', dob: '', password: '', customerType: 'customer', isVerified: false,
  businessName: '', businessType: 'other', businessRegistrationNumber: '', gstNumber: '', panNumber: '',
  emailPromotion: true, whatsappPromotion: true, appNotification: true,
};

const fromCustomer = (c) => ({
  ...emptyForm,
  name: c.name || '', email: c.email || '', mobile: c.mobile || '', gender: c.gender || '',
  dob: c.dob ? String(c.dob).slice(0, 10) : '',
  customerType: c.customerType || 'customer', isVerified: !!c.isVerified,
  businessName: c.businessName || '', businessType: c.businessType || 'other',
  businessRegistrationNumber: c.businessRegistrationNumber || '', gstNumber: c.gstNumber || '', panNumber: c.panNumber || '',
  emailPromotion: c.emailPromotion !== false, whatsappPromotion: c.whatsappPromotion !== false, appNotification: c.appNotification !== false,
});

// `customer` set = edit, empty = create
function CustomerForm({ customer = null, onBack, onSaved }) {
  const isEdit = !!customer;
  const [form, setForm] = useState(customer ? fromCustomer(customer) : emptyForm);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(customer?.image || '');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const onText = (e) => {
    set({ [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors((p) => ({ ...p, [e.target.name]: undefined }));
  };

  const pickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return setErrors((p) => ({ ...p, image: 'Please choose an image file' }));
    if (file.size > 5 * 1024 * 1024) return setErrors((p) => ({ ...p, image: 'The image must be under 5 MB' }));
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setErrors((p) => ({ ...p, image: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ''))) e.mobile = 'Enter 10 digits';
    if (!isEdit) {
      if (!form.password) e.password = 'Password is required';
      else if (form.password.length < 6) e.password = 'Use at least 6 characters';
    }
    if (form.customerType === 'business') {
      if (!form.businessName.trim()) e.businessName = 'Business name is required';
      if (form.gstNumber && !GST_RX.test(form.gstNumber.trim().toUpperCase())) e.gstNumber = 'That does not look like a valid 15-character GST number';
      if (form.panNumber && !PAN_RX.test(form.panNumber.trim().toUpperCase())) e.panNumber = 'A PAN looks like ABCDE1234F';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setFormError('');
    if (!validate()) return setFormError('Please fix the highlighted fields.');
    setSaving(true);
    try {
      const fd = new FormData();
      const business = form.customerType === 'business';
      const values = {
        name: form.name.trim(), email: form.email.trim(), mobile: form.mobile.replace(/\D/g, ''), gender: form.gender, dob: form.dob,
        customerType: form.customerType, isVerified: form.isVerified,
        emailPromotion: form.emailPromotion, whatsappPromotion: form.whatsappPromotion, appNotification: form.appNotification,
        ...(business ? { businessName: form.businessName.trim(), businessType: form.businessType, businessRegistrationNumber: form.businessRegistrationNumber.trim(), gstNumber: form.gstNumber.trim().toUpperCase(), panNumber: form.panNumber.trim().toUpperCase() } : {}),
        ...(isEdit ? {} : { password: form.password }),
      };
      Object.entries(values).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
      if (image) fd.append('image', image);
      if (isEdit) await putData(`customer/update/${customer._id}`, fd, authToken());
      else await postFileUpload('customer/add', fd, authToken());
      onSaved();
    } catch (err) {
      setFormError(err?.message || 'Could not save the customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={onBack} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a', lineHeight: 1.2 }}>{isEdit ? 'Edit Customer' : 'Add Customer'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{isEdit ? form.name : 'Create an account for a customer or a business buyer.'}</Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={submit} noValidate>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Section title="Profile" hint="Who this customer is" accent="#6366f1" icon={MdPerson}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar src={preview || undefined} sx={{ width: 76, height: 76, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: 26 }}>{initials(form.name)}</Avatar>
                      <IconButton component="label" size="small" sx={{ position: 'absolute', right: -4, bottom: -4, background: '#0f172a', color: '#fff', '&:hover': { background: '#334155' } }}>
                        <MdCameraAlt size={16} />
                        <input type="file" hidden accept="image/*" onChange={(e) => pickImage(e.target.files?.[0])} />
                      </IconButton>
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14 }}>Profile photo</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: errors.image ? '#dc2626' : '#64748b' }}>{errors.image || 'Optional · JPG or PNG up to 5 MB'}</Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Full name" name="name" required value={form.name} onChange={onText} error={!!errors.name} helperText={errors.name} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" name="email" type="email" required value={form.email} onChange={onText} error={!!errors.email} helperText={errors.email} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mobile number" name="mobile" required value={form.mobile} onChange={onText} error={!!errors.mobile} helperText={errors.mobile || '10 digits'} inputProps={{ maxLength: 12 }} /></Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField select fullWidth size="small" label="Gender" name="gender" value={form.gender} onChange={onText}>
                        <MenuItem value="">Not set</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Date of birth" name="dob" type="date" InputLabelProps={{ shrink: true }} value={form.dob} onChange={onText} inputProps={{ max: new Date().toISOString().slice(0, 10) }} /></Grid>
                  </Grid>
                </Section>

                <Section title="Account" hint="Type of customer and sign-in details" accent="#10b981" icon={MdVerifiedUser}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#64748b', mb: 0.5 }}>Customer type</Typography>
                      <ToggleButtonGroup exclusive fullWidth size="small" value={form.customerType} onChange={(_, v) => v && set({ customerType: v })}>
                        <ToggleButton value="customer" sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700 }}>Customer</ToggleButton>
                        <ToggleButton value="business" sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700 }}>Business</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {!isEdit ? (
                        <TextField fullWidth size="small" label="Password" name="password" type="password" required value={form.password} onChange={onText} error={!!errors.password} helperText={errors.password || 'At least 6 characters. Share it with the customer securely.'} />
                      ) : (
                        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', pt: 2.2 }}>Passwords can't be changed here. The customer can reset it from the app.</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={form.isVerified} onChange={(e) => set({ isVerified: e.target.checked })} sx={switchSx} />}
                        label={<Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>Mark as verified {isEdit ? '' : '(activates the account right away)'}</Typography>}
                      />
                    </Grid>
                  </Grid>
                </Section>

                {form.customerType === 'business' && (
                  <Section title="Business details" hint="Used for invoices and GST" accent="#0ea5e9" icon={MdBusiness}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Business name" name="businessName" required value={form.businessName} onChange={onText} error={!!errors.businessName} helperText={errors.businessName} /></Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth size="small" label="Business type" name="businessType" value={form.businessType} onChange={onText}>
                          {BUSINESS_TYPES.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="GST number" name="gstNumber" value={form.gstNumber} onChange={(e) => onText({ target: { name: 'gstNumber', value: e.target.value.toUpperCase() } })} error={!!errors.gstNumber} helperText={errors.gstNumber || 'Optional'} inputProps={{ maxLength: 15, style: { fontFamily: 'monospace' } }} /></Grid>
                      <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="PAN" name="panNumber" value={form.panNumber} onChange={(e) => onText({ target: { name: 'panNumber', value: e.target.value.toUpperCase() } })} error={!!errors.panNumber} helperText={errors.panNumber || 'Optional'} inputProps={{ maxLength: 10, style: { fontFamily: 'monospace' } }} /></Grid>
                      <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Registration number" name="businessRegistrationNumber" value={form.businessRegistrationNumber} onChange={onText} helperText="Optional" /></Grid>
                    </Grid>
                  </Section>
                )}

                <Section title="Communication" hint="What this customer has agreed to receive" accent="#f59e0b" icon={MdNotifications}>
                  {[
                    ['emailPromotion', 'Promotional emails', 'Offers and updates by email'],
                    ['whatsappPromotion', 'WhatsApp promotions', 'Offers and updates on WhatsApp'],
                    ['appNotification', 'App notifications', 'Order updates and alerts in the app'],
                  ].map(([key, label, sub]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                      <Box>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{label}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{sub}</Typography>
                      </Box>
                      <Switch checked={form[key]} onChange={(e) => set({ [key]: e.target.checked })} sx={switchSx} />
                    </Box>
                  ))}
                </Section>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <Avatar src={preview || undefined} sx={{ width: 72, height: 72, mx: 'auto', mb: 1.25, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: 24 }}>{initials(form.name)}</Avatar>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16 }}>{form.name || 'New customer'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{form.email || 'email'} · {form.mobile || 'mobile'}</Typography>
                  {form.customerType === 'business' && form.businessName && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#0369a1', fontWeight: 700, mt: 0.5 }}>{form.businessName}</Typography>}
                </Paper>
                {formError && <Alert severity="error">{formError}</Alert>}
                <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: FONT, textTransform: 'none', background: 'rgb(255 209 2)', color: 'black', py: 1.2, borderRadius: '12px', fontWeight: 800, fontSize: 14, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>
                  {saving ? <><CircularProgress size={18} sx={{ color: 'black', mr: 1 }} />Saving…</> : isEdit ? 'Update customer' : 'Add customer'}
                </Button>
                <Button onClick={onBack} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default CustomerForm;
